import { useState } from 'react';
import { Copy } from 'lucide-react';
import { Button, Input, Textarea, Checkbox, IconButton, Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateVpc, useUpdateVpc, useCreateSubnet, useUpdateSubnet, type Vpc as VpcT } from '@/api/opennebula';

/** RFC 1918 private network-address check (matches the legacy console). */
export function isValidPrivateCidr(cidr: string): boolean {
  const m = cidr.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/);
  if (!m) return false;
  const oct = [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4])];
  const prefix = Number(m[5]);
  if (oct.some((o) => o > 255) || prefix < 8 || prefix > 30) return false;
  const [a, b] = oct;
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
}

/** An IP value with a copy-to-clipboard affordance. */
export function CopyIp({ ip }: { ip: string }) {
  const { show } = useToast();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <code style={{ fontVariantNumeric: 'tabular-nums' }}>{ip}</code>
      <IconButton variant="ghost" size="sm" aria-label="Copy IP" onClick={() => { navigator.clipboard?.writeText(ip); show({ tone: 'success', title: 'Copied' }); }}>
        <Copy size={13} />
      </IconButton>
    </span>
  );
}

/** VPC create dialog mirroring the legacy console: name, RFC1918 CIDR, description, shared. */
export function CreateVpcDialog({ open, onOpenChange, serviceId }: { open: boolean; onOpenChange: (o: boolean) => void; serviceId: string }) {
  const { show } = useToast();
  const [name, setName] = useState('');
  const [cidr, setCidr] = useState('');
  const [description, setDescription] = useState('');
  const [isShared, setIsShared] = useState(false);

  const reset = () => { setName(''); setCidr(''); setDescription(''); setIsShared(false); };
  const create = useCreateVpc(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'VPC created' }); reset(); onOpenChange(false); },
    onError: () => show({ tone: 'danger', title: 'Create failed' }),
  });

  const cidrTouched = cidr.trim().length > 0;
  const cidrOk = isValidPrivateCidr(cidr.trim());
  const valid = !!name.trim() && cidrOk;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>New VPC</DialogTitle></DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            Create a Virtual Private Cloud to isolate and organize your network resources. A VPC gives you full
            control over IP addressing, subnets, and routing.
          </p>
          <Field label="VPC Name" required>
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your VPC name" />}
          </Field>
          <Field
            label="CIDR Block"
            required
            description="RFC 1918 private range (10.x, 172.16–31.x, 192.168.x)"
            error={cidrTouched && !cidrOk ? 'Enter a valid private network address, e.g. 10.0.0.0/16.' : undefined}
          >
            {(p) => <Input {...p} value={cidr} onChange={(e) => setCidr(e.target.value)} placeholder="e.g., 10.0.0.0/16" />}
          </Field>
          <Field label="Description">
            {(p) => <Textarea {...p} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Enter VPC description" />}
          </Field>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>
            <Checkbox checked={isShared} onCheckedChange={(c) => setIsShared(c === true)} />
            <span>
              <span style={{ fontWeight: 600 }}>Make this VPC shared</span>
              <span style={{ display: 'block', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)' }}>
                Visibility label only — does not change access control.
              </span>
            </span>
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!valid || create.isPending} onClick={() => create.mutate({ name: name.trim(), cidr: cidr.trim(), description: description.trim() || undefined, is_shared: isShared })}>
            Create VPC
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditVpcDialog({ serviceId, vpc, onClose }: { serviceId: string; vpc: VpcT; onClose: () => void }) {
  const { show } = useToast();
  const [name, setName] = useState(vpc.name ?? '');
  const [description, setDescription] = useState(vpc.description ?? '');
  const [isShared, setIsShared] = useState(!!vpc.is_shared);

  const update = useUpdateVpc(serviceId, vpc.id, {
    onSuccess: () => { show({ tone: 'success', title: 'VPC updated' }); onClose(); },
    onError: () => show({ tone: 'danger', title: 'Update failed' }),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit VPC</DialogTitle></DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Field label="Name">{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label="Description">{(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />}</Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--vn-font-size-sm)' }}>
            <Checkbox checked={isShared} onCheckedChange={(c) => setIsShared(c === true)} />
            Make this VPC shared across the organization
          </label>
          <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>CIDR ({vpc.cidr || '—'}) cannot be changed after creation.</p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!name.trim() || update.isPending} onClick={() => update.mutate({ name: name.trim(), description: description || undefined, is_shared: isShared })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Create a subnet within a VPC (multi-field + RFC1918 CIDR validation → dialog,
 *  matching the legacy console and the "New pool" create-dialog convention). */
export function CreateSubnetDialog({ open, onOpenChange, serviceId, vpcId }: { open: boolean; onOpenChange: (o: boolean) => void; serviceId: string; vpcId: string }) {
  const { show } = useToast();
  const [name, setName] = useState('');
  const [cidr, setCidr] = useState('');
  const [description, setDescription] = useState('');
  const [isShared, setIsShared] = useState(false);

  const reset = () => { setName(''); setCidr(''); setDescription(''); setIsShared(false); };
  const add = useCreateSubnet(serviceId, vpcId, {
    onSuccess: () => { show({ tone: 'success', title: 'Subnet added' }); reset(); onOpenChange(false); },
    onError: () => show({ tone: 'danger', title: 'Add failed' }),
  });

  const cidrTouched = cidr.trim().length > 0;
  const cidrOk = isValidPrivateCidr(cidr.trim());
  const valid = !!name.trim() && cidrOk;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add subnet</DialogTitle></DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Field label="Subnet name" required>{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. web-tier" />}</Field>
          <Field
            label="CIDR Block"
            required
            description="A subnet within the VPC CIDR (RFC 1918 private range)"
            error={cidrTouched && !cidrOk ? 'Enter a valid private CIDR, e.g. 10.0.1.0/24.' : undefined}
          >
            {(p) => <Input {...p} value={cidr} onChange={(e) => setCidr(e.target.value)} placeholder="e.g., 10.0.1.0/24" />}
          </Field>
          <Field label="Description">{(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />}</Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>
            <Checkbox checked={isShared} onCheckedChange={(c) => setIsShared(c === true)} />
            Shared subnet
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!valid || add.isPending} onClick={() => add.mutate({ name: name.trim(), cidr: cidr.trim(), description: description.trim() || undefined, is_shared: isShared })}>Add subnet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditSubnetDialog({ serviceId, vpcId, subnet, onClose }: { serviceId: string; vpcId: string; subnet: { id: string; name?: string; description?: string; is_shared?: boolean }; onClose: () => void }) {
  const { show } = useToast();
  const [name, setName] = useState(subnet.name ?? '');
  const [description, setDescription] = useState(subnet.description ?? '');
  const [isShared, setIsShared] = useState(!!subnet.is_shared);

  const update = useUpdateSubnet(serviceId, vpcId, subnet.id, {
    onSuccess: () => { show({ tone: 'success', title: 'Subnet updated' }); onClose(); },
    onError: () => show({ tone: 'danger', title: 'Update failed' }),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit subnet</DialogTitle></DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Field label="Name">{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label="Description">{(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />}</Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--vn-font-size-sm)' }}>
            <Checkbox checked={isShared} onCheckedChange={(c) => setIsShared(c === true)} />
            Shared subnet
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!name.trim() || update.isPending} onClick={() => update.mutate({ name: name.trim(), description: description || undefined, is_shared: isShared })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
